import dayjs from 'dayjs';

import {
    MortgageDocument,
    getTransactionTypeFromPurpose,
} from '@ratehub/documents/mtg-journey-document/stable';


/**
 * @param {Object} sessionStore
 * @param {String} id - if also passed purpose, will fetch document even if not same purpose
 * @param {String} purpose
 */
async function findMortgageJourneyDocument(sessionStore, { id, purpose }) {
    if (id) {
        return findDocumentById(sessionStore, id);
    }

    if (purpose) {
        return findMostRecentDocumentByPurpose(sessionStore, purpose);
    }

    return null;
}

function findMostRecentDocumentByPurpose(sessionStore, purpose) {
    const mostRecentDocument = sessionStore
        .getDocumentMetadataByPath('mortgages.journey')
        .filter(metadata => metadata) // ignore non-persisted documents
        .filter(filterDocumentsByMetadata)
        .sort(sortDocumentsByMostRecentLastEditDate)
        .find(findSameTransactionTypeFromMetadata(purpose));

    return mostRecentDocument
        ? findDocumentById(sessionStore, mostRecentDocument.id)
        : null;
}

function findDocumentById(sessionStore, id) {
    const found = sessionStore
        .getDocumentMetadataByPath('mortgages.journey')
        .filter(metadata => metadata) // ignore non-persisted documents
        .filter(metadata => !isDeleted(metadata))
        .find(metadata => metadata.id === id);

    return found
        ? sessionStore.fetchDocumentById(id, document => MortgageDocument.create(document))
        : null;
}

function sortDocumentsByMostRecentLastEditDate(a, b) {
    const aLastModified = getDocumentDate(a);
    const bLastModified = getDocumentDate(b);

    // newer comes first (b - a)
    return bLastModified.diff(aLastModified);
}

function filterDocumentsByMetadata(metadata) {
    // ignore if a document doesn't have this metadata because it's probably a legacy document (ie. older than 30 days)
    // soft-deleted documents are also not fetched
    if (!hasRequiredMetadata(metadata) || isDeleted(metadata)) {
        return false;
    }

    const today = dayjs().startOf('day');
    const lastEditDate = getDocumentDate(metadata).startOf('day');

    return today.diff(lastEditDate, 'days') <= 30;
}

function isDeleted(metadata) {
    return metadata.deleted === true;
}

function findSameTransactionTypeFromMetadata(purpose) {
    const transactionType = getTransactionTypeFromPurpose(purpose);

    return metadata => {
        // TODO: Remove this when we're on v4
        const schemaVersion = parseInt(metadata.schemaVersion);

        const currentPurpose = schemaVersion === 4
            ? metadata.purpose
            : metadata.purpose.id;

        return getTransactionTypeFromPurpose(currentPurpose) === transactionType;
    };
}

// lastEditDate may be missing from document, so fallback to updatedAt (always provided by profile)
// TODO: determine which fields we actually want to use (eg. lastEditDateClient)
function getDocumentDate(metadata) {
    const date = metadata.lastEditDate ?? metadata.updatedAt;

    return dayjs(date);
}

function hasRequiredMetadata(metadata) {
    // Non-persisted documents dont have metadata
    if (!metadata) {
        return;
    }

    const metadataFields = Object.keys(metadata);

    return REQUIRED_METADATA_KEYS
        .every(requiredKey => metadataFields.includes(requiredKey));
}

const REQUIRED_METADATA_KEYS = [ 'purpose' ];

export default findMortgageJourneyDocument;
